import * as React from 'react'
import {
  MAX_SESSION_AGE_MS,
  THRESHOLD_FOR_SCORE_INCREASE,
} from '../utils/commentScoreConstants'
import { getScoreSessionKeys } from '../utils/commentScoreSessionManager'

export const useCommentScoreTracker = (
  currentScore: number | null,
  channelId: string,
): {
  hasScoreIncreased: boolean
  scoreDelta: number
  startSession: () => void
  stopSession: () => void
} => {
  const [scoreDelta, setScoreDelta] = React.useState(0)
  const [hasScoreIncreased, setHasScoreIncreased] = React.useState(false)
  const startScoreRef = React.useRef<number | null>(null)

  const { scoreKey, timestampKey } = getScoreSessionKeys(channelId)

  const resetScore = React.useCallback((): void => {
    localStorage.removeItem(scoreKey)
    localStorage.removeItem(timestampKey)
    startScoreRef.current = null
    setScoreDelta(0)
    setHasScoreIncreased(false)
  }, [scoreKey, timestampKey])

  const startSession = (): void => {
    const existingScore = localStorage.getItem(scoreKey)
    const existingTimestamp = localStorage.getItem(timestampKey)
    const now = Date.now()

    if (existingScore !== null && existingTimestamp !== null) {
      const timestamp = parseInt(existingTimestamp, 10)
      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (!isExpired) return
      // Clear stale session
      resetScore()
    }

    if (currentScore != null) {
      localStorage.setItem(scoreKey, currentScore.toString())
      localStorage.setItem(timestampKey, now.toString())
      startScoreRef.current = currentScore
    }
  }

  const stopSession = (): void => {
    resetScore()
  }

  // Rehydrate from localStorage on mount
  React.useEffect(() => {
    const stored = localStorage.getItem(scoreKey)
    const storedTimestamp = localStorage.getItem(timestampKey)

    if (stored && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10)
      const now = Date.now()

      const isExpired = now - timestamp > MAX_SESSION_AGE_MS
      if (isExpired) {
        resetScore()
        return
      }

      startScoreRef.current = parseInt(stored, 10)
    }
  }, [scoreKey, timestampKey, resetScore])

  // Check for score increase
  React.useEffect(() => {
    if (
      currentScore != null &&
      startScoreRef.current != null &&
      currentScore > startScoreRef.current
    ) {
      const delta = currentScore - startScoreRef.current
      setScoreDelta(delta)
      setHasScoreIncreased(delta >= THRESHOLD_FOR_SCORE_INCREASE)
    }
  }, [currentScore])

  return { hasScoreIncreased, scoreDelta, startSession, stopSession }
}
